import { useCallback, useMemo } from "react";
import { useAuthDispatch, useAuthSelector } from "../state/AuthStoreProvider";
import type { AuthRootState } from "../state/store";
import { logOutUserFromFirebase } from "../state/thunks/logOutThunk";
import {
  getTokenUserInfo,
  getTokenFacilityInfo,
  getTokenRoleInfo,
  isCurrentUserAdmin,
  isCurrentUserSuperAdmin,
  userLoggedInFacilityType,
  getCurrentFacilityId,
} from "../services/memberManagementService";
import type { UseAuthResult, UseAuthUser, UseAuthFacility, UseAuthRole } from "../types";

export function useAuth(): UseAuthResult {
  const dispatch = useAuthDispatch();

  const { user: authUser, verifyingUser, isLoading } = useAuthSelector(
    (state: AuthRootState) => state.firebaseAuthentication,
  );

  const isAuthenticated = verifyingUser === "verified" && authUser !== null;

  const user = useMemo<UseAuthUser | null>(() => {
    if (!isAuthenticated || !authUser) return null;

    const tokenUser = getTokenUserInfo();

    return {
      id: authUser.id,
      email: authUser.email ?? "",
      firstName: tokenUser?.FirstName ?? "",
      lastName: tokenUser?.LastName ?? "",
      profilePicUrl: tokenUser?.ProfilePicUrl ?? "",
      status: tokenUser?.Status ?? "",
      isAdmin: isCurrentUserAdmin(),
      isSuperAdmin: isCurrentUserSuperAdmin(),
    };
  }, [isAuthenticated, authUser]);

  const facility = useMemo<UseAuthFacility | null>(() => {
    if (!isAuthenticated) return null;

    const tokenFacility = getTokenFacilityInfo();
    const facilityId = getCurrentFacilityId();
    if (!tokenFacility || !facilityId) return null;

    return {
      id: facilityId,
      name: tokenFacility.name,
      type: userLoggedInFacilityType(facilityId),
      internalFacilities: tokenFacility.internal_facilities ?? [],
      externalFacilities: tokenFacility.external_facilities ?? [],
    };
  }, [isAuthenticated]);

  const role = useMemo<UseAuthRole | null>(() => {
    if (!isAuthenticated) return null;

    const tokenRole = getTokenRoleInfo();
    if (!tokenRole) return null;

    return {
      id: tokenRole.id,
      name: tokenRole.name,
      permissions: tokenRole.permissions,
    };
  }, [isAuthenticated]);

  const logout = useCallback(async () => {
    await dispatch(logOutUserFromFirebase());
  }, [dispatch]);

  return {
    isAuthenticated,
    isLoading,
    verifyingUser,
    logout,
    user,
    facility,
    role,
  };
}
